import * as Sentry from '@sentry/react';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Bug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/sentry-test')({
  component: () => (
    <ProtectedRoute>
      <SentryTestPage />
    </ProtectedRoute>
  ),
});

function SentryTestPage() {
  const { t } = useTranslation();

  const throwSyncError = () => {
    throw new Error('Test synchronous error for Sentry');
  };

  const throwAsyncError = async () => {
    await new Promise((resolve) => setTimeout(resolve, 100));
    throw new Error('Test asynchronous error for Sentry');
  };

  const captureManualError = () => {
    try {
      throw new Error('Test manual capture for Sentry');
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Bug className="h-6 w-6" />
        <h1 className="text-2xl font-bold">{t('sentryTest.title')}</h1>
      </div>

      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertTriangle className="h-5 w-5" />
            <p className="font-medium">{t('sentryTest.warning')}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t('sentryTest.syncErrorTitle')}</CardTitle>
            <CardDescription>{t('sentryTest.syncErrorDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={throwSyncError} variant="destructive" className="w-full">
              {t('sentryTest.triggerError')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('sentryTest.asyncErrorTitle')}</CardTitle>
            <CardDescription>{t('sentryTest.asyncErrorDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => throwAsyncError()} variant="destructive" className="w-full">
              {t('sentryTest.triggerError')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('sentryTest.manualCaptureTitle')}</CardTitle>
            <CardDescription>{t('sentryTest.manualCaptureDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={captureManualError} variant="destructive" className="w-full">
              {t('sentryTest.triggerError')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('sentryTest.instructions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <ol className="list-decimal list-inside space-y-2">
            <li>{t('sentryTest.step1')}</li>
            <li>{t('sentryTest.step2')}</li>
            <li>{t('sentryTest.step3')}</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
